import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  Add as AddIcon
} from '@mui/icons-material';
import { chargersService } from '../services/api';
import { ProjectAsset } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useOrgNavigation } from '../utils/navigation';
import AddAssetRequestModal from '../components/AddAssetRequestModal';

const Units: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { navigateToOrgPath } = useOrgNavigation();
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddAssetOpen, setIsAddAssetOpen] = useState(false);

  useEffect(() => {
    const fetchAssets = async () => {
      if (!user?.orgId || !projectId) {
        setError('No organization ID available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await chargersService.getProjectAssets(projectId, user.orgId);
        
        if (response.success && response.data) {
          setAssets(response.data.assets);
        } else {
          setError('Failed to fetch project assets');
        }
      } catch (err) {
        console.error('Error fetching project assets:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch project assets');
      } finally {
        setLoading(false);
      }
    };

    fetchAssets();
  }, [user?.orgId, projectId]);

  const getChargerImage = (unit_name: string, type: string) => {
    // Generic asset image placeholder for now
    if (type === 'excavator') {
      return process.env.PUBLIC_URL + '/unit-pictures/excavator.jpg';
    } else if (unit_name.includes('KE1')) {
      return process.env.PUBLIC_URL + '/unit-pictures/thor.avif';
    } else if (unit_name.includes('KE4')) {
      return process.env.PUBLIC_URL + '/unit-pictures/mjolner.jpg';
    } else if (unit_name.includes('HB')) {
      return process.env.PUBLIC_URL + '/unit-pictures/hb.jpg';
    } else if (unit_name.includes('BC')) {
      return process.env.PUBLIC_URL + '/unit-pictures/boostcharger.png';
    } else {
      return process.env.PUBLIC_URL + '/unit-pictures/zaptec-pro.png';
    }
  };


  const handleUnitClick = (unitId: string) => {
    const chargerPath = `/assets/chargers/${unitId}`;
    const orgPath = navigateToOrgPath(chargerPath);
    navigate(orgPath);
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Assets
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsAddAssetOpen(true)}
          sx={{
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
            '& .MuiButton-label': {
              color: '#FFF',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '22px',
              letterSpacing: '0.46px',
              textTransform: 'uppercase',
            },
          }}
        >
          ADD ASSET
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && assets.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '240px' }}>
          <CircularProgress />
        </Box>
      )}

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
        gap: '24px'
      }}>
        {[...assets]
          .sort((a, b) => (a.type || '').localeCompare(b.type || '', undefined, { sensitivity: 'base' }))
          .map((asset) => (
          <Card 
            key={asset.unit_id}
            onClick={() => handleUnitClick(asset.unit_id)}
            sx={{ 
              width: '100%',
              height: '320px',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              }
            }}
          >
            <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Top Section - Unit Picture */}
              <Box sx={{ 
                height: '180px',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#f0f0f0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img 
                  src={getChargerImage(asset.unit_name, asset.type)} 
                  alt={asset.unit_name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                {/* Status indicator overlay */}
                {/* <Box sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1
                }}>
                  <Chip
                    label={'NA'}
                    size="small"
                    sx={{
                      backgroundColor: '#9E9E9E',
                      color: 'white',
                      fontWeight: 500,
                      fontSize: '0.75rem'
                    }}
                  />
                </Box> */}
              </Box>

              {/* Middle Section - Specs */}
               <Box sx={{ 
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-around',
                px: 2,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#f8f9fa',
                flexShrink: 0
              }}>
                {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LightningIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    NA
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <img 
                    src={process.env.PUBLIC_URL + '/grid.svg'} 
                    alt="Grid"
                    style={{
                      width: '16px',
                      height: '16px',
                      filter: 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%)'
                    }}
                  />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    NA
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ViewIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    N/A
                  </Typography>
                </Box> */}
              </Box> 

              {/* Bottom Section - Unit Name and Details */}
              <Box sx={{ 
                minHeight: '80px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                flex: 1
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: theme.palette.text.primary,
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '1rem',
                  lineHeight: 1.2
                }}>
                  {asset.unit_name}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  mb: 0.5,
                  fontSize: '0.875rem',
                  lineHeight: 1.2
                }}>
                  {asset.type}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
      <AddAssetRequestModal 
        open={isAddAssetOpen} 
        onClose={() => setIsAddAssetOpen(false)} 
      />
    </Box>
  );
};

export default Units;
